// src/components/PushNotificationSettings.js

import React, { useState, useEffect } from 'react';

const PushNotificationSettings = ({
  pushNotificationStatus,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  sendTestPushNotification
}) => {
  const [loading, setLoading] = useState(false);

  // Collapsible state with localStorage persistence for entire section
  const [sectionCollapsed, setSectionCollapsed] = useState(() => {
    const saved = localStorage.getItem('backgroundNotificationsCollapsed');
    return saved !== null ? JSON.parse(saved) : false; // Default to expanded
  });

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('backgroundNotificationsCollapsed', JSON.stringify(sectionCollapsed));
  }, [sectionCollapsed]);

  const toggleSectionCollapsed = () => {
    setSectionCollapsed(prev => !prev);
  };

  const handleSubscribe = async () => {
    setLoading(true);
    await subscribeToPushNotifications();
    setLoading(false);
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    await unsubscribeFromPushNotifications();
    setLoading(false);
  };

  const handleTestNotification = async () => {
    setLoading(true);
    await sendTestPushNotification();
    setLoading(false);
  };

  const getStatusColor = () => {
    if (!pushNotificationStatus.supported) return 'text-red-500';
    if (pushNotificationStatus.subscribed) return 'text-green-500';
    if (pushNotificationStatus.permission === 'denied') return 'text-red-500';
    return 'text-yellow-500';
  };

  const getStatusText = () => {
    if (!pushNotificationStatus.supported) return 'Not Supported';
    if (pushNotificationStatus.subscribed) return 'Enabled';
    if (pushNotificationStatus.permission === 'denied') return 'Blocked';
    if (pushNotificationStatus.permission === 'granted') return 'Ready to Enable';
    return 'Not Enabled';
  };

  if (!pushNotificationStatus.supported) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <span className="text-red-400 text-xl">❌</span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Push Notifications Not Supported
            </h3>
            <p className="mt-1 text-xs text-red-700">
              Your device or browser doesn't support background push notifications.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Collapsible Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors rounded-t-lg"
        onClick={toggleSectionCollapsed}
      >
        <div className="flex items-center">
          <h3 className="text-sm font-medium text-gray-900">
            Background Notifications
          </h3>
          <span className={`ml-2 text-xs font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
        
        <button
          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          title={sectionCollapsed ? "Expand notifications section" : "Collapse notifications section"}
        >
          <span className={`transform transition-transform duration-200 inline-block ${sectionCollapsed ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>
      </div>

      {/* Collapsible Content */}
      {!sectionCollapsed && (
        <div className="px-4 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="mt-1 text-xs text-gray-600">
                Ensure you are signed in on your browser to receive garbage truck alerts even when the app is closed or in the background.
              </p>

              {pushNotificationStatus.permission === 'denied' && (
                <p className="mt-2 text-xs text-red-600">
                  ⚠️ Notifications are blocked. Enable them in your browser settings.
                </p>
              )}
            </div>

            <div className="ml-4 flex flex-col space-y-2">
              {!pushNotificationStatus.subscribed ? (
                <button
                  onClick={handleSubscribe}
                  disabled={loading || pushNotificationStatus.permission === 'denied'}
                  className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '⏳' : '🔔'} Enable
                </button>
              ) : (
                <button
                  onClick={handleUnsubscribe}
                  disabled={loading}
                  className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? '⏳' : '🔕'} Disable
                </button>
              )}

            </div>
          </div>

          {pushNotificationStatus.subscribed && (
            <div className="mt-3 p-2 bg-green-50 rounded border border-green-200">
              <p className="text-xs text-green-700">
                ✅ You're all set! Garbage truck notifications will appear even when the app is closed.
              </p>
            </div>
          )}

          {!pushNotificationStatus.subscribed && pushNotificationStatus.permission !== 'denied' && (
            <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-700">
                💡 Enable background notifications to never miss the garbage truck!
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PushNotificationSettings;